/**
 * Audit-Log (#1138 Tag 3) — Filter + CSV-Export.
 */
import { useEffect, useMemo, useState } from 'react';

const API = import.meta.env.VITE_API_URL || '';

const ACTION_LABELS = {
  login: 'Login',
  login_fail: 'Login Fehler',
  login_rate_limited: 'Rate-Limit',
  logout: 'Logout',
  change_password: 'PW geändert',
  change_password_fail: 'PW-Änderung Fehler',
  edit_mapping: 'Mapping bearbeitet',
  edit_plan: 'Plan bearbeitet',
  edit_aggregate: 'Aggregat bearbeitet',
  edit_annahme: 'Annahme bearbeitet',
  create_user: 'User erstellt',
  edit_user: 'User bearbeitet',
  reset_user_password: 'PW zurückgesetzt',
};

function actionColor(a) {
  if (a.includes('fail') || a.includes('rate_limited')) return 'text-red-600 bg-red-50';
  if (a.startsWith('edit_') || a === 'change_password') return 'text-amber-700 bg-amber-50';
  if (a.startsWith('create_') || a === 'reset_user_password') return 'text-[#338edc] bg-[#338edc]/10';
  return 'text-[#4B5563] bg-[#F1F2F4]';
}

export default function InvestorAuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('');
  const [limit, setLimit] = useState(100);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      const res = await fetch(`${API}/api/investor/audit-log?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setRows(data.rows || []);
      }
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [limit]); // eslint-disable-line

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (filterAction !== 'all' && r.action !== filterAction) return false;
      if (filterUser && !(r.user_email || '').toLowerCase().includes(filterUser.toLowerCase())) return false;
      return true;
    });
  }, [rows, filterAction, filterUser]);

  const actions = useMemo(() => {
    const set = new Set(rows.map(r => r.action));
    return Array.from(set).sort();
  }, [rows]);

  function exportCsv() {
    const headers = ['id', 'created_at', 'user_email', 'action', 'ip_address', 'details'];
    const lines = [headers.join(';')];
    for (const r of filtered) {
      const details = r.details ? JSON.stringify(r.details).replace(/"/g, '""') : '';
      lines.push([
        r.id,
        new Date(r.created_at).toISOString(),
        r.user_email || '',
        r.action,
        r.ip_address || '',
        `"${details}"`,
      ].join(';'));
    }
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `weship-investor-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-lg font-semibold text-[#1A1A2E]">Audit-Log</h1>
        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="text-xs px-3 py-1.5 rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F8F9FC] disabled:opacity-60"
        >
          CSV-Export ({filtered.length})
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <select value={filterAction} onChange={e => setFilterAction(e.target.value)} className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white">
          <option value="all">Alle Aktionen</option>
          {actions.map(a => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
        </select>
        <input type="text" placeholder="User-Email…" value={filterUser} onChange={e => setFilterUser(e.target.value)} className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white" />
        <select value={limit} onChange={e => setLimit(parseInt(e.target.value, 10))} className="text-xs border border-[#E5E7EB] rounded-md px-2 py-1.5 bg-white">
          <option value={50}>letzte 50</option>
          <option value={100}>letzte 100</option>
          <option value={250}>letzte 250</option>
          <option value={500}>letzte 500</option>
        </select>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
            <tr>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280] w-40">Zeit</th>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">User</th>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Aktion</th>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280] w-32">IP</th>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Details</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-xs text-[#9CA3AF]">Lädt…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-xs text-[#9CA3AF]">Keine Einträge</td></tr>
            ) : filtered.map(r => (
              <tr key={r.id} className="border-t border-[#F1F2F4] align-top">
                <td className="px-4 py-2 text-xs text-[#6B7280] tabular-nums whitespace-nowrap">
                  {new Date(r.created_at).toLocaleString('de-AT', { dateStyle: 'short', timeStyle: 'medium' })}
                </td>
                <td className="px-4 py-2 text-xs text-[#1A1A2E]">{r.user_email || <span className="italic text-[#9CA3AF]">system</span>}</td>
                <td className="px-4 py-2 text-xs">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${actionColor(r.action)}`}>
                    {ACTION_LABELS[r.action] || r.action}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-[#6B7280] font-mono">{r.ip_address || '–'}</td>
                <td className="px-4 py-2 text-xs text-[#4B5563]">
                  {r.details ? (
                    <code className="text-[10px] text-[#6B7280] break-all">{JSON.stringify(r.details)}</code>
                  ) : <span className="text-[#9CA3AF]">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
